/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { produce } from "immer";
import { createLocalZustand } from "./LocalZustand";

export type HandleChangeStore<T> = (
  partial:
    | T
    | Partial<T>
    | ((state: T) => T | Partial<T>),
  replace?: boolean | undefined
) => void;

export type HandleChangeValues<V> = (value: V) => void;

/**
 * Standard action set shared by every local-zustand form store: the current
 * `values`, raw `set`/`get`, and the `setValues`/`updateValues` helpers that
 * replace or shallow-merge the values slice via immer.
 */
export interface FormStoreBase<V> {
  values: V;
  set: HandleChangeStore<FormStoreBase<V>>;
  get: () => FormStoreBase<V>;
  setValues: HandleChangeValues<V>;
  updateValues: HandleChangeValues<V>;
}

/**
 * createFormStore function.
 *
 * Generic local-zustand form store factory: absorbs the standard
 * values/set/get/setValues/updateValues action set (the same boilerplate as
 * `FastContext`) so individual forms only need to supply the actions that are
 * genuinely specific to them.
 *
 * @param {*} buildExtraActions - optional callback receiving (set, get) and
 * returning any extra actions to merge into the store, beyond the standard set.
 *
 * @returns {*} the local-zustand context object (Context/Provider/Consumer/useStore).
 */
export function createFormStore<T extends FormStoreBase<any>>(
  buildExtraActions?: (
    set: HandleChangeStore<T>,
    get: () => T,
  ) => Omit<T, keyof FormStoreBase<any>>
) {
  type Values = T["values"];

  return createLocalZustand<T>((initializer) => (set, get) => {
    const typedSet = set as unknown as HandleChangeStore<T>;
    const typedGet = get as unknown as () => T;

    const baseActions = {
      values: { ...(initializer.values as Values) },

      set: typedSet,
      get: typedGet,

      setValues: (values: Values) =>
        set((state) =>
          produce(state, (draft: any) => {
            draft.values = values;
          })
        ),

      updateValues: (newValues: Values) =>
        set((state) => {
          const { values } = get();
          return produce(state, (draft: any) => {
            draft.values = { ...(values as Values), ...newValues };
          });
        }),
    };

    const extraActions = buildExtraActions
      ? buildExtraActions(typedSet, typedGet)
      : ({} as Omit<T, keyof FormStoreBase<any>>);

    return { ...baseActions, ...extraActions } as unknown as T;
  });
}

export default createFormStore;
